import {
    makeStyles,
    createStyles,
    Theme,
} from '@bb-ui/react-library/dist/components/styles';

/**
 * Shared styling for the Authentication tab. Spacing comes from theme.spacing(),
 * and inline-logical properties (marginInlineStart, not marginLeft) are used
 * throughout so right-to-left layouts need no second stylesheet.
 */
export const useStyles = makeStyles(
    (theme: Theme) =>
        createStyles({
            section: {
                marginBottom: theme.spacing(5),
            },
            sectionHeading: {
                margin: theme.spacing(0, 0, 1, 0),
                '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: theme.spacing(0.5),
                },
            },
            sectionIntro: {
                marginBottom: theme.spacing(2),
                maxWidth: '60ch',
            },
            addIdentityProviderButton: {
                width: '180px',
                alignSelf: 'flex-end',
                padding: '0.3125rem 0.9375rem',
                textDecoration: 'none',
                color: theme.palette.text.primary,
                border: `1px solid ${theme.palette.text.primary}`,
                borderRadius: '0.125rem',
                textAlign: 'center',
                justifyContent: 'center',
                backgroundColor: 'transparent',
                '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: '2px',
                },
            },
            /** Actions header over the row icons; `end`, not `right`, for RTL. */
            actionsHeaderCell: {
                textAlign: 'end',
            },
            toolbar: {
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                rowGap: theme.spacing(1),
                marginBottom: theme.spacing(2),
            },
            tableContainer: {
                overflowX: 'auto',
            },
            actionsContainer: {
                display: 'flex',
                justifyContent: 'flex-end',
                alignItems: 'center',
                width: '100%',
                height: '100%',
            },
            actionContainer: {
                '&:not(:last-child)': {
                    marginRight: theme.spacing(1),
                },
            },
            actionIconWrapper: {
                width: '24px',
                height: '24px',
                alignItems: 'center',
                display: 'flex',
                justifyContent: 'center',
            },
            actionIcon: {
                color: theme.palette.action.active,
            },
            actionIconWrapperBuiltIn: {
                cursor: 'default',
                '& svg': {
                    color: theme.palette.action.disabled,
                },
            },
            statusCell: {
                display: 'flex',
                alignItems: 'center',
                columnGap: theme.spacing(1),
                minHeight: theme.spacing(4),
            },
            statusFailed: {
                color: theme.palette.error.main,
                fontWeight: 600,
            },
            /** Status above its reason: the column is narrow, error detail is not. */
            statusFailedCell: {
                display: 'flex',
                flexDirection: 'column',
                rowGap: theme.spacing(0.25),
                paddingBlock: theme.spacing(0.5),
            },
            statusFailedReason: {
                color: theme.palette.text.secondary,
                fontSize: theme.typography.caption.fontSize,
            },
            bannerAction: {
                marginInlineStart: theme.spacing(2),
            },
            emptyState: {
                padding: theme.spacing(4),
                textAlign: 'center',
                color: theme.palette.text.secondary,
            },
            banner: {
                marginBottom: theme.spacing(2),
            },
            dialogConfirmContent: {
                minHeight: '160px',
                justifyContent: 'center',
                display: 'flex',
                flexDirection: 'column',
            },
            checkingSpinner: {
                display: 'inline-flex',
                verticalAlign: 'middle',
                marginRight: theme.spacing(1),
            },
            wizardPaper: {
                width: '64rem !important',
                // Never wider than the viewport; on a narrow window this wins.
                maxWidth: 'calc(100% - 4rem) !important',
            },
            // Wizard
            dialogContent: {
                display: 'grid',
                gridTemplateColumns: '1fr',
                rowGap: theme.spacing(2),
            },
            stepLabel: {
                color: theme.palette.text.secondary,
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '0.75rem',
            },
            stepList: {
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: theme.spacing(1),
                listStyle: 'none',
                margin: theme.spacing(0, 0, 1, 0),
                padding: 0,
            },
            stepItem: {
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(0.75),
                color: theme.palette.text.secondary,
                fontSize: '0.8125rem',
                // Separator drawn before every step but the first, so the list holds only items.
                '& + &::before': {
                    content: '""',
                    width: '20px',
                    height: '1px',
                    marginRight: theme.spacing(0.25),
                    backgroundColor: theme.palette.divider,
                    flex: 'none',
                },
            },
            stepItemCurrent: {
                color: theme.palette.text.primary,
                fontWeight: 600,
            },
            stepNumber: {
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '20px',
                height: '20px',
                borderRadius: '50%',
                border: `1px solid ${theme.palette.divider}`,
                fontSize: '0.6875rem',
                lineHeight: 1,
            },
            stepNumberCurrent: {
                borderColor: theme.palette.primary.main,
                backgroundColor: theme.palette.primary.main,
                color: theme.palette.primary.contrastText,
            },
            stepNumberDone: {
                borderColor: theme.palette.success.main,
                color: theme.palette.success.main,
            },
            typeGrid: {
                display: 'grid',
                gridTemplateColumns:
                    'repeat(auto-fit, minmax(max(12rem, (100% - 1rem) / 3), 1fr))',
                gap: theme.spacing(1),
                width: '100%',
            },
            typeCard: {
                margin: 0,
                alignItems: 'flex-start',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '0.125rem',
                padding: theme.spacing(1, 1.5, 1, 0.5),
            },
            typeCardSelected: {
                borderColor: theme.palette.primary.main,
            },
            typeCardName: {
                display: 'block',
                fontWeight: 600,
            },
            typeCardDesc: {
                display: 'block',
                color: theme.palette.text.secondary,
                fontSize: '0.75rem',
            },
            fieldWarning: {
                display: 'block',
                marginBlockStart: theme.spacing(0.5),
                fontWeight: 600,
            },
            notEditableBanner: {
                '&&': {
                    backgroundColor: theme.palette.warning.light,
                    color: theme.palette.warning.contrastText,
                },
                '&& svg': {
                    color: theme.palette.warning.contrastText,
                },
            },
            certificateExpiry: {
                ...theme.typography.caption,
                color: theme.palette.text.secondary,
            },
            certificateExpiryValid: {
                color: theme.palette.success.main,
            },
            certificateExpiryExpired: {
                color: theme.palette.error.main,
            },
            fieldColumn: {
                display: 'grid',
                gridTemplateColumns: '1fr',
                rowGap: theme.spacing(2),
                alignItems: 'start',
                minWidth: 0,
            },
            guideCard: {
                padding: theme.spacing(2),
                backgroundColor: theme.palette.background.default,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '0.125rem',
                minWidth: 0,
            },
            guideTypeChoices: {
                alignItems: 'center',
                columnGap: theme.spacing(2),
                rowGap: theme.spacing(0.5),
                marginBottom: theme.spacing(1.5),
            },
            guideSteps: {
                margin: theme.spacing(1, 0, 2, 0),
                paddingInlineStart: theme.spacing(2.5),
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(1),
                fontSize: '0.8125rem',
            },
            monoValue: {
                fontFamily: 'monospace',
                fontSize: '0.75rem',
                overflowWrap: 'anywhere',
            },
            definitionList: {
                margin: 0,
                display: 'grid',
                gridTemplateColumns: '1fr',
                rowGap: theme.spacing(1),
            },
            definitionRow: {
                display: 'grid',
                gridTemplateColumns: 'minmax(120px, 1fr) minmax(0, 2fr)',
                columnGap: theme.spacing(2),
                paddingBottom: theme.spacing(1),
                borderBottom: `1px solid ${theme.palette.divider}`,
            },
            definitionRowPlain: {
                paddingBottom: 0,
                borderBottom: 'none',
            },
            spSection: {
                paddingTop: theme.spacing(2),
                borderTop: `1px solid ${theme.palette.divider}`,
            },
            definitionTerm: {
                color: theme.palette.text.secondary,
                fontSize: '0.8125rem',
                margin: 0,
            },
            definitionValue: {
                margin: 0,
                overflowWrap: 'anywhere',
            },
            /** Provisioning stages. Same ordered-list-plus-aria-current pattern. */
            stageList: {
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: theme.spacing(1.5),
                listStyle: 'none',
                margin: theme.spacing(1, 0, 2, 0),
                padding: 0,
            },
            stageItem: {
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing(0.75),
                color: theme.palette.text.secondary,
                fontSize: '0.8125rem',
                minHeight: theme.spacing(3),
            },
            stageItemCurrent: {
                color: theme.palette.text.primary,
                fontWeight: 600,
            },
            stageDot: {
                display: 'inline-block',
                width: '10px',
                height: '10px',
                borderRadius: '50%',
                border: `1px solid ${theme.palette.divider}`,
            },
            stageDotDone: {
                backgroundColor: theme.palette.success.main,
                borderColor: theme.palette.success.main,
            },
            stageIcon: {
                color: theme.palette.success.main,
                width: '16px',
                height: '16px',
            },
            stepHeading: {
                margin: theme.spacing(0.5, 0, 0, 0),
                '&:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: theme.spacing(0.5),
                },
            },
            guidance: {
                marginTop: theme.spacing(1),
            },
            testPaper: {
                width: '34rem !important',
                maxWidth: 'calc(100% - 4rem) !important',
            },
            testSteps: {
                margin: 0,
                paddingInlineStart: theme.spacing(3),
                display: 'grid',
                rowGap: theme.spacing(0.5),
            },
            testLinkRow: {
                display: 'flex',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: theme.spacing(2),
            },
            // Lets the copy status line wrap under the link and the button.
            testLinkCopy: {
                display: 'contents',
            },
            testLinkCopyStatus: {
                flexBasis: '100%',
                marginTop: -theme.spacing(1),
            },
            testTroubleshooting: {
                '& summary': {
                    cursor: 'pointer',
                    marginBottom: theme.spacing(1),
                },
            },
            toolbarStart: {
                display: 'flex',
                gap: theme.spacing(1),
                marginBottom: theme.spacing(2),
            },
            // Audit log
            auditPanel: {
                marginBottom: theme.spacing(5),
                padding: theme.spacing(0, 2, 0, 2),
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: '4px',
            },
            auditHeading: {
                margin: 0,
            },
            // Spaced as the expansion panel summary it replaces.
            auditToggle: {
                width: '100%',
                minHeight: 48,
                justifyContent: 'space-between',
                gap: theme.spacing(1),
                padding: theme.spacing(2, 2),
                font: 'inherit',
                textAlign: 'start',
            },
            auditChevron: {
                flexShrink: 0,
                transition: 'transform 150ms',
            },
            auditChevronOpen: {
                transform: 'rotate(180deg)',
            },
            auditDetails: {
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                width: '100%',
                minWidth: 0,
                padding: theme.spacing(1, 0, 2, 0),
            },
            // The details column aligns its children to the start, so anything
            // meant to span the panel has to say so, as the table does.
            auditFullWidth: {
                alignSelf: 'stretch',
                width: '100%',
            },
            auditTableContainer: {
                alignSelf: 'stretch',
                width: '100%',
                minWidth: 0,
                maxWidth: '100%',
                overflowX: 'auto',
                '& tbody tr:focus-visible': {
                    outline: `2px solid ${theme.palette.primary.main}`,
                    outlineOffset: '-2px',
                },
            },
            timestampCell: {
                overflowWrap: 'anywhere',
                fontVariantNumeric: 'tabular-nums',
            },
            auditTable: {
                tableLayout: 'fixed',
                width: '100%',
                minWidth: 720,
            },
            timestampHeaderCell: {
                width: '18%',
            },
            actionCell: {
                width: '18%',
                overflowWrap: 'anywhere',
            },
            performedByCell: {
                width: '18%',
                overflowWrap: 'anywhere',
            },
            providerCell: {
                width: '18%',
                overflowWrap: 'anywhere',
            },
            detailsCell: {
                width: '28%',
            },
            // One line, clipped; AuditDetailsValue opens the rest in a dialog.
            detailsValue: {
                display: 'block',
                minWidth: 0,
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
            },
            // The width overflow is measured against.
            detailsFit: {
                display: 'block',
                minWidth: 0,
            },
            detailsButton: {
                display: 'flex',
                alignItems: 'center',
                width: '100%',
                padding: 0,
                margin: 0,
                border: 'none',
                background: 'none',
                font: 'inherit',
                color: 'inherit',
                textAlign: 'left',
                cursor: 'pointer',
                // Transparent rather than none: forced colours drop the shadow
                // but draw this outline in the system colour.
                outline: '2px solid transparent',
                '&:focus': {
                    boxShadow: `0px 0px 0px 2px ${theme.palette.focus.main}`,
                },
            },
            detailsExpandIcon: {
                flexShrink: 0,
                marginLeft: theme.spacing(0.5),
                width: 14,
                height: 14,
                color: theme.palette.text.secondary,
            },
            // tabIndex={0} + role="region" on this element makes it keyboard-scrollable.
            detailsDialogBox: {
                width: '100%',
                boxSizing: 'border-box',
                maxHeight: 320,
                overflowY: 'auto',
                border: `1px solid ${theme.palette.border.main}`,
                borderRadius: 4,
                backgroundColor: theme.palette.background.b3,
                padding: theme.spacing(1.5),
                // Transparent rather than none: forced colours drop the shadow
                // but draw this outline in the system colour.
                outline: '2px solid transparent',
                '&:focus': {
                    boxShadow: `0px 0px 0px 2px ${theme.palette.focus.main}`,
                },
            },
            loadMore: {
                marginTop: theme.spacing(2),
            },
            loadMoreSpinner: {
                marginRight: theme.spacing(1),
            },
            detailsLines: {
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(0.5),
            },
            // Metadata URLs have no spaces to wrap at.
            detailsLine: {
                overflowWrap: 'anywhere',
            },
        }),
    {
        name: 'Authentication-',
    },
);
